import * as React from 'react';
import { Fabric } from '@fluentui/react';
import { getTheme, ITheme, mergeStyleSets } from '@fluentui/react/lib/Styling';
import { Kunde, KundeFilter, useKundeList } from 'api/kunde';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import Map, { Marker, Popup } from 'react-map-gl';
import { createKundeUrl } from 'utils/formatter';
import { useSelector } from 'react-redux';
import { authSelector } from 'store/auth/selector';

// @ts-ignore
// eslint-disable-next-line import/no-webpack-loader-syntax, @typescript-eslint/no-var-requires
// mapboxgl = require('worker-loader!mapbox-gl/dist').default;

// mapboxgl.workerClass = require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default;

const theme: ITheme = getTheme();
const { palette } = theme;

const styles = mergeStyleSets({
    container: {
        position: 'relative',
    },
    popup: {
        top: 10,
        left: 10,
    },
    pinMine: {
        cursor: 'pointer',
        stroke: '#333',
        fill: palette.green,
    },
    pinKunde: {
        cursor: 'pointer',
        stroke: '#333',
        fill: palette.blueLight,
    },
    pinKundeLieferant: {
        cursor: 'pointer',
        stroke: '#333',
        fill: palette.blueLight,
    },
    pinInteressent: {
        cursor: 'pointer',
        stroke: '#333',
        fill: palette.black,
    },
    pinLieferant: {
        cursor: 'pointer',
        stroke: '#333',
        fill: palette.yellow,
    },
    pinMuster: {
        cursor: 'pointer',
        stroke: '#333',
        fill: palette.red,
    },
    pinGM: {
        cursor: 'pointer',
        stroke: 'red',
        strokeWidth: 2,
        fill: 'white',
    },
    pinSonstiges: {
        cursor: 'pointer',
        stroke: '#333',
        fill: palette.white,
    },
    pinOther: {
        cursor: 'pointer',
        stroke: '#333',
        fill: '#888',
    },
    pinSelected: {
        cursor: 'pointer',
        stroke: '#333',
        fill: palette.blue,
    },
    openButton: {
        cursor: 'pointer',
        background: 'none',
        border: 'none',
        padding: 0,
        textDecoration: 'underline',
    },
});

const MAPBOX_TOKEN = 'pk.eyJ1IjoiaHBldGVyc2lsa2EiLCJhIjoiY2wwemhxNzg0MW9iejNva2JxdWhqcHV4byJ9.fAuLabcszrtadn_FRCm-DQ';

// eslint-disable-next-line max-len
const ICON = `M20.2,15.7L20.2,15.7c1.1-1.6,1.8-3.6,1.8-5.7c0-5.6-4.5-10-10-10S2,4.5,2,10c0,2,0.6,3.9,1.6,5.4c0,0.1,0.1,0.2,0.2,0.3
  c0,0,0.1,0.1,0.1,0.2c0.2,0.3,0.4,0.6,0.7,0.9c2.6,3.1,7.4,7.6,7.4,7.6s4.8-4.5,7.4-7.5c0.2-0.3,0.5-0.6,0.7-0.9
  C20.1,15.8,20.2,15.8,20.2,15.7z`;

interface PinProps {
    size?: number;
    onClick?: () => void;
    onMouseOver?: () => void;
    className: string;
}

function Pin({ size = 20, onClick, onMouseOver, className }: PinProps) {
    return (
        <svg
            height={size}
            style={{ marginLeft: -9 }}
            viewBox="0 0 24 24"
            className={className}
            onClick={onClick}
            onMouseOver={onMouseOver}
        >
            <path d={ICON} />
        </svg>
    );
}

interface KundeKarteProps {
    kundeFilter: KundeFilter | null;
    selectedKunde: Kunde | null;
    onSelectKunde: (kunde: Kunde | null) => void;
}

interface Viewport {
    width: string;
    height: string;
    latitude: number;
    longitude: number;
    zoom: 4;
}

export const KundeKarte: React.FC<KundeKarteProps> = ({ kundeFilter, selectedKunde, onSelectKunde }) => {
    const auth = useSelector(authSelector);
    const mapFilter = React.useMemo<KundeFilter>(() => {
        return {
            ...kundeFilter,
            updateGeoPoints: true,
        };
    }, [kundeFilter]);

    const kundeList = useKundeList(mapFilter, { enabled: false });

    React.useEffect(() => {
        kundeList.refetch().then();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapFilter]);

    const [viewport, setViewport] = React.useState<Viewport>({
        width: '100%',
        height: '100%',
        latitude: 45,
        longitude: 9,
        zoom: 4,
    });

    const pins = React.useMemo(
        () =>
            (kundeList.data || [])
                .filter((kunde) => kunde.latitude !== null && kunde.longitude !== null)
                .map((kunde, index) => {
                    let className = styles.pinOther;
                    if (kunde.betreuer === auth.nr || kunde.telBetreuer === auth.nr) {
                        switch (kunde.art) {
                            case 'I':
                                // Interessent
                                className = styles.pinInteressent;
                                break;
                            case 'K':
                                // Kunde
                                className = styles.pinKunde;
                                break;
                            case 'KL':
                                // Kunde/Lieferant
                                className = styles.pinKundeLieferant;
                                break;
                            case 'L':
                                // Lieferant
                                className = styles.pinLieferant;
                                break;
                            case 'M':
                                // Muster
                                className = styles.pinMuster;
                                break;
                            case 'GM':
                                className = styles.pinGM;
                                break;
                            default:
                                // Sonstiges
                                className = styles.pinSonstiges;
                        }
                    }

                    if (kunde.nr === selectedKunde?.nr) {
                        className = styles.pinSelected;
                    }

                    return (
                        <Marker
                            key={`marker-${index as number}`}
                            longitude={kunde.longitude || 0}
                            latitude={kunde.latitude || 0}
                        >
                            <Pin
                                className={className}
                                onMouseOver={() => {
                                    if (kunde.nr !== selectedKunde?.nr) onSelectKunde(kunde);
                                }}
                            />
                        </Marker>
                    );
                }),
        [kundeList.data, auth.nr, selectedKunde?.nr, onSelectKunde],
    );

    return (
        <Fabric className={styles.container}>
            {kundeList.isLoading || (kundeList.isFetching && <LoadingIndicator />)}
            <Map
                viewState={viewport}
                onViewStateChange={({ viewState }: { viewState: Viewport }): void => setViewport(viewState)}
                reuseMaps
                mapStyle="mapbox://styles/mapbox/streets-v11"
                preventStyleDiffing
                mapboxApiAccessToken={MAPBOX_TOKEN}
                width="100%"
                height="100%"
            >
                {pins}
                {selectedKunde && (
                    <Popup
                        anchor="bottom"
                        className={styles.popup}
                        longitude={Number(selectedKunde.longitude)}
                        latitude={Number(selectedKunde.latitude)}
                        closeOnClick={false}
                        onClose={() => onSelectKunde(null)}
                    >
                        <div>
                            <div>
                                <button
                                    type="button"
                                    className={styles.openButton}
                                    onClick={() => {
                                        if (selectedKunde) window.open(createKundeUrl(selectedKunde.nr.toString()));
                                    }}
                                >
                                    <strong>{selectedKunde.name1}</strong>
                                </button>
                            </div>
                            <div>
                                {selectedKunde.plz} {selectedKunde.ort}, {selectedKunde.strasse}
                            </div>
                        </div>
                    </Popup>
                )}
            </Map>
        </Fabric>
    );
};
